import { ref, computed, watch, reactive, toRefs } from 'vue'
import { defineStore } from 'pinia'
import { useApiStore } from '@/stores/Api'
import { useElectronStore } from '@/stores/Electron'

import logger from '@/utilities/Logger'
import axios, { AxiosError } from 'axios'
import globalEvents from '@/utilities/GlobalEvents'
import { bool, sleep, getUnique } from '@/utilities/Tools'
import _i18n from '@/utilities/I18n'
import { CryptoStorage } from '@/utilities/CryptoStorage'

import type { ValidateTokenExtendedResponse } from '@/TcxApi/Authentication'
import { PbxSettingsCallerIdNameOutbound, type PbxSettingsResponse } from '@/TcxApi/PbxSettings'
import { SectionType, type RuntimeSettings } from '@/stores/Settings.types'
import { PbxAppSettingsIntegrationRunWhen, PbxAppSettingsMediaPreferredHeadset, type PbxAppSettingsDesktopResponse } from '@/TcxApi/PbxApp'
import { EmployeeFeature, EmployeeRecordingMode, EmployeeRecordingQuality, EmployeeAccessLevel, EmployeeAccessLevelNumber } from '@/TcxApi/Employee'
import { type PbxBeaconListResponse, type PbxBeacon } from '@/TcxApi/PbxBeacon'
import type { QuestionModalMessage } from '@/definitions/Common'
import { type PbxSipPhoneHotdeskGrabByIdResponse, type PbxSipPhone, type PbxSipPhoneHotdeskGrabByIdRequest } from '@/TcxApi/PbxSipPhone'

export const useSettingsStore = defineStore('settings', () => {
	const i18n = _i18n.global
	const api = useApiStore()
	const launched = new Date()
	const ignoreSettingsChanges = ref(true)
	const mainInitCompleted = ref(false)

	const auth = ref<ValidateTokenExtendedResponse | null>(null)
	const pbxSettings = ref<PbxSettingsResponse>(reactive(initialPbxSettings()))
	const settings = ref<PbxAppSettingsDesktopResponse>(reactive(initialSettings()))
	const runtime = ref<RuntimeSettings>(reactive(initialRuntimeSettings()))
	const beacons = ref<PbxBeacon[]>([])

	const recordingsModalVisible = ref<boolean>(false)
	const voicemailModalVisible = ref<boolean>(false)
	const cdrModalVisible = ref<boolean>(false)
	const errorModalVisible = ref<boolean>(false)
	const questionModalVisible = ref<boolean>(false)
	const questionModalCurrent = ref<QuestionModalMessage | undefined>(undefined)
	const pickAPhoneVisible = ref<boolean>(false)
	const isAnEmployeeContextMenuVisible = ref<boolean>(false)
	const authenticatedAt = ref<Date | null>(null)

	/** API token */
	const token = ref<string | null>(runtime.value.rememberMe ? localStorage.getItem('token') : sessionStorage.getItem('token'))

	/** Are we in development mode */
	const isDevelopment = computed(() => window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')

	/** URL for the API */
	const apiUrl = computed(() => (isDevelopment.value ? 'http://localhost:3000' : 'https://api.telecomx.dk'))

	/** Is the user authenticated */
	const isAuthenticated = computed(() => !!auth.value?.employee)

	/** Id of the phone that is currently being used */
	const realPhoneId = computed(() => settings.value.softphoneId ?? settings.value.phoneId)

	/** Are we in debug mode */
	const isInDebugMode = computed(() => isAuthenticated.value && auth.value?.employeeFeatures.includes(EmployeeFeature.COMMUNICATOR_DEBUG))

	const accessLevel = computed(() => auth.value?.accessLevel ?? EmployeeAccessLevel.NO_LOGIN)

	/** Number of seconds the user has been logged in */
	const authenticatedSeconds = computed(() => {
		if (!isAuthenticated.value || !authenticatedAt.value) { return -1 }
		return Math.floor((new Date().getTime() - authenticatedAt.value.getTime()) / 1000)
	})

	/**
	 * Check if the user has at least the required access level
	 * @param level Minimum required access level
	 * @returns True if the user has at least the required access level
	 */
	const accessLevelIsMin = function(level: EmployeeAccessLevel) {
		const has = EmployeeAccessLevelNumber[auth?.value?.accessLevel ?? EmployeeAccessLevel.NO_LOGIN]
		const min = EmployeeAccessLevelNumber[level]

		return has >= min
	}

	/** May the user edit dashboards */
	const isDashboardEditor = computed(() => isAuthenticated.value && (auth.value?.employeeFeatures.includes(EmployeeFeature.DASHBOARD_EDITOR) || accessLevelIsMin(EmployeeAccessLevel.MANAGER)))

	/** May the user transcribe recordings - employees below manager need the permission from the PBX settings */
	const canTranscribeRecordings = computed(() => accessLevelIsMin(EmployeeAccessLevel.MANAGER) || pbxSettings.value.recording?.permissions?.transcribe === true)

	/** May the user have recordings summarized by the AI - employees below manager need the permission from the PBX settings */
	const canSummarizeRecordings = computed(() => accessLevelIsMin(EmployeeAccessLevel.MANAGER) || pbxSettings.value.recording?.permissions?.summarize === true)

	// #region Persistence

	let saveSettingsDebouncer: ReturnType<typeof setTimeout> | null = null

	// Save to server when something has changed - debounced 1 second
	watch(settings, () => {
		if (ignoreSettingsChanges.value) {
			logger.info('Settings: Ignoring settings changes')
			return
		}

		logger.info('Settings: Settings changed - saving to server')

		if (isAuthenticated.value && token.value) {
			if (saveSettingsDebouncer) { clearTimeout(saveSettingsDebouncer) }
			saveSettingsDebouncer = setTimeout(async () => {
				saveSettingsDebouncer = null

				try {
					if (isAuthenticated.value && token.value) {
						logger.info('Settings: Saving to server')
						await api.post(`/pbx/app/settings/desktop/${runtime.value.instanceId}`, settings.value)
						logger.info('Settings: Settings saved to server')
					}
				}
				catch (err) {
					logger.error(`Settings: Failed to save settings to server: ${(err as Error).message}`)
					logger.error(JSON.stringify(err))
					console.log(err) // eslint-disable-line
				}
			}, 1000)
		}
	}, { deep: true })

	// Persistent rememberMe, token and emailAddress to local storage
	watch(() => runtime.value.rememberMe, (newValue) => {
		localStorage.setItem('rememberMe', newValue.toString())
	})

	watch(token, (newValue) => {
		logger.debug(`Settings: Token changed - ${runtime.value.rememberMe ? 'clearing' : 'saving'} in ${runtime.value.rememberMe ? 'local storage' : 'session storage'}`)
		if (runtime.value.rememberMe) {
			if (newValue) {
				const cryptoStorage = new CryptoStorage(getInstanceId())

				cryptoStorage.encrypt(newValue).then(encrypted => {
					localStorage.setItem('token', encrypted)
				}).catch(() => { /** Ignore */ })
			} else {
				localStorage.removeItem('token')
			}
		} else if (newValue) {
			const cryptoStorage = new CryptoStorage(getInstanceId())

			cryptoStorage.encrypt(newValue).then(encrypted => {
				sessionStorage.setItem('token', encrypted)
			}).catch(() => { /** Ignore */ })
		} else {
			sessionStorage.removeItem('token')
		}
	})

	watch(() => runtime.value.emailAddress, (newValue) => { localStorage.setItem('emailAddress', newValue ?? '') })

	// #endregion

	// #region Logout and clear

	/** Invoked to clear all auth data because we are no longer logged in */
	function clear() {
		auth.value = null // must be first
		token.value = null // and second
		// The token watcher picks its storage from rememberMe, which is reset just below and so is already false by the time
		// the watcher runs - it would only ever clear sessionStorage. Clear both here, so a remembered token cannot linger
		localStorage.removeItem('token')
		sessionStorage.removeItem('token')
		runtime.value.emailAddress = undefined
		runtime.value.rememberMe = false
		ignoreSettingsChanges.value = true
		pbxSettings.value = initialPbxSettings()
		settings.value = initialSettings()
	}

	/**
	 * Invoked when user wants to logout
	 * - Will reset most settings
	 * @param stash True to stash settings, so that we may restore them after an impersonation session has ended
	 * @param loggedOutAlreadyEmitted True if Auth:LoggedOut has already been emitted and should not be emitted again
	 */
	function logout(stash?: boolean, loggedOutAlreadyEmitted?: boolean) {
		if (stash) {
			stashSettings()
			localStorage.removeItem('instanceId') // when impersonating we want to be someone else - thus we need a new instance id
			runtime.value.instanceId = getInstanceId()
		}

		logger.info('Settings: Performing logout')
		clear()
		Object.keys(localStorage).forEach(key => {
			if (key.startsWith('Generic-')) {
				localStorage.removeItem(key)
			}
		})

		_i18n.global.locale.value = navigator.language.split('-')[0] as 'da' | 'en'

		api.init(apiUrl.value, '')

		logger.info('Settings: Emitted Auth:LoggedOut')
		if (!loggedOutAlreadyEmitted) {
			globalEvents.emit('Auth:LoggedOut', 'SETTINGS')
		}
	}

	// #endregion

	// #region Impersonation

	/** Stash settings so that we can do an impersonation session, and return to ourself afterwards */
	async function stashSettings() {
		logger.info('Settings: Stashing settings')
		const cryptoStorage = new CryptoStorage(getInstanceId())
		const encryptedToken = await cryptoStorage.encrypt(token.value as string)

		localStorage.setItem('stash-token', encryptedToken)
		localStorage.setItem('stash-rememberMe', runtime.value.rememberMe.toString())
		localStorage.setItem('stash-emailAddress', runtime.value.emailAddress as string)
		localStorage.setItem('stash-instanceId', runtime.value.instanceId)

		const generic: Record<string, string> = {}

		for (const key of Object.keys(localStorage)) {
			if (key.startsWith('Generic-')) {
				generic[key] = localStorage.getItem(key) as string
			}
		}
		sessionStorage.setItem('stash-generic', JSON.stringify(generic))
		runtime.value.gotStash = true
	}

	/** Restore settings after we completed an impersonation session */
	async function restoreSettings() {
		logger.info('Settings: Restoring stashed settings')
		const generic: Record<string, string> = JSON.parse(sessionStorage.getItem('stash-generic') as string)

		for (const key in generic) {
			localStorage.setItem(key, generic[key] as string)
		}

		runtime.value.instanceId = localStorage.getItem('stash-instanceId') as string // Must be before the cryptoStorage

		const encryptedToken = localStorage.getItem('stash-token')
		const cryptoStorage = new CryptoStorage(getInstanceId())

		token.value = await cryptoStorage.decrypt(encryptedToken as string)
		runtime.value.rememberMe = bool(localStorage.getItem('stash-rememberMe') as string)
		runtime.value.emailAddress = localStorage.getItem('stash-emailAddress') ?? undefined
		runtime.value.gotStash = false
	}

	/** Invoke to end impersonation and return to the original session */
	function endImpersonation() {
		if (runtime.value.gotStash) {
			logout()
			setTimeout(() => {
				restoreSettings()
				loadAuth()
			}, 500)
		}
	}

	// #endregion

	/** Load settings from server */
	async function loadSettings() {
		try {
			// Load Pbx settings from server
			const res2 = await api.get<PbxSettingsResponse>('/pbx/settings')

			pbxSettings.value = res2.data
			logger.info('Settings: PBX settings loaded')

			// Load settings from server
			const res = await api.get<PbxAppSettingsDesktopResponse>(`/pbx/app/settings/desktop/${runtime.value.instanceId}`)

			settings.value = res.data
			logger.info('Settings: Settings loaded')

			if (runtime.value.isEmbedded) {
				const res3 = await api.get<PbxBeaconListResponse>('/pbx/beacon?limit=500&shared=true')

				beacons.value = res3.data.beacons
				logger.info('Settings: Beacons loaded')
			} else {
				logger.info('Settings: Not embedded - not loading beacons')
			}

			if (settings.value.fullscreen) {
				if (runtime.value.isEmbedded) {
					const electron = useElectronStore()

					electron.setFullscreen(settings.value.fullscreen)
				} else {
					document.documentElement.requestFullscreen()
				}
				logger.info('Settings: Enabled fullscreen mode')
			}

			// Start beacon scanner if needed
			if (settings.value.beaconsMayUpdateLocation) {
				const electron = useElectronStore()
				const uuids = getUnique<string>(beacons.value.map(b => b.uuid))

				if (uuids.length === 1) {
					electron.setBeaconScanner(true, uuids[0])
				} else {
					electron.setBeaconScanner(true)
				}
			}

			await sleep(100)
			ignoreSettingsChanges.value = false

			await checkIfWeNeedToConvertOldSettingsToServerSettings()
			await initGlobalShortcuts()

			if (!realPhoneId.value && ((new Date().getTime() - launched.getTime()) / 1000) < 30) {
				globalEvents.emit('PickAPhone')
			}
		}
		catch (err) {
			logger.error(`Settings: Failed to load desktop/pbx settings from server: ${(err as Error).message}`)
		}
	}

	/** Handles checking for old settings and converting them to new server based settings */
	async function checkIfWeNeedToConvertOldSettingsToServerSettings() {
		if (localStorage.getItem('skin')) {
			// We have old settings - convert them
			settings.value.phoneId = localStorage.getItem('myPhoneId') ?? null
			settings.value.dashboardId = localStorage.getItem('myDashboardId') ?? null
			settings.value.windowWallboardMode = bool(localStorage.getItem('windowWallboardMode') as string)
			settings.value.presenceBackground = bool(localStorage.getItem('presenceBackground') as string)
			settings.value.fullscreen = bool(localStorage.getItem('fullscreen') as string)
			runtime.value.instanceId = localStorage.getItem('instanceId') ?? getInstanceId()
			settings.value.ringtoneExternal = localStorage.getItem('ringtone') ?? '60af91d485fa792a658f75e5'
			settings.value.ringtoneLocal = localStorage.getItem('ringtoneLocal') ?? '619e5e4daa69b00a66f57f75'
			settings.value.showStatusPanel = bool(localStorage.getItem('showStatusPanel') as string)
			settings.value.autoLaunch = bool(localStorage.getItem('autolaunch') as string)
			settings.value.autoLaunchHide = bool(localStorage.getItem('autolaunchHidden') as string)
			settings.value.queueCriticalSound = bool(localStorage.getItem('queueCriticalSound') as string)
			settings.value.dashboardColumnWidth = Number(localStorage.getItem('dashboardColumnWidth')) || 370
			settings.value.theme = localStorage.getItem('skin') ?? 'EARTH'

			try { settings.value.globalShortcuts = JSON.parse(localStorage.getItem('globalShortcuts') as string) }
			catch (_) { /** Ignore */ }

			try { settings.value.integration = JSON.parse(localStorage.getItem('integration') as string) }
			catch (_) { /** Ignore */ }

			const preferredRaw = localStorage.getItem('MediaDevices:Preferred')
			if (preferredRaw) {
				try {
					const preferred = JSON.parse(preferredRaw)

					settings.value.media.preferredAudioInputs = preferred.audioInputs as string[]
					settings.value.media.preferredAudioOutputs = preferred.audioOutputs as string[]
					settings.value.media.preferredVideoInputs = preferred.videoInputs as string[]
					settings.value.media.preferredRingerOutputs = preferred.ringerOutputs as string[]
					settings.value.media.ringerVolume = preferred.ringerVolume as number
				}
				catch (_) { /** Ignore */ }
			}

			settings.value.media.kuando.use = bool(localStorage.getItem('KuandoUse') as string)
			settings.value.media.kuando.ringTone = Number(localStorage.getItem('KuandoRingTone')) || 0
			settings.value.media.kuando.ringVolume = Number(localStorage.getItem('KuandoRingVolume')) || 0

			// Cleanup - a declined passkey offer is kept, so a decline is not undone and the offer stays away for its 90 days
			for (const key of Object.keys(localStorage)) {
				if (!key.startsWith('Generic-') && !key.startsWith('passkeyOfferDeclinedAt:') && key !== 'rememberMe' && key !== 'token' && key !== 'emailAddress') {
					localStorage.removeItem(key)
				}
			}
		}
	}

	/**
	 * Load authentication data from the server - which in turn will trigger the load of all other data
	 * @param impersonateToken Token of current session for an employee to impersonate
	 */
	async function loadAuth(impersonateToken?: string, throwError = false) {
		if (impersonateToken) {
			logger.info('Settings: Impersonating engaged')
			token.value = impersonateToken
			runtime.value.rememberMe = false
		} else {
			runtime.value.gotStash = false
		}

		if (!token.value) { return }

		try {
			const res = await axios.get<ValidateTokenExtendedResponse>(`${apiUrl.value}/auth/validateToken?extend=true&token=${token.value}`, { timeout: 5000 })
			if (!res.data.success || !res.data.communicatorAccess) {
				logout()
			} else {
				if (impersonateToken) { runtime.value.emailAddress = undefined }
				logger.info('Settings: Token validated and core settings received from server')
				await initAfterLoginOrValidate(res.data)
			}
		}
		catch (err) {
			if ((err as AxiosError).code === 'ECONNABORTED' || (err as AxiosError).code === 'ERR_NETWORK') {
				if (!questionModalVisible.value) {
					globalEvents.emit('CommonQuestionModal', {
						header: i18n.t('common.error.commErrorHeader'),
						message: i18n.t('common.error.commErrorMessage2'),
						ok: i18n.t('sipClient.errorRetry'),
						cancel: i18n.t('sipClient.errorTerminate'),
						emit: 'Network:Error:Retry',
						cancelledEmit: 'Network:Error:Terminate'
					})
				}
			} else {
				token.value = null // Reset token if we failed to validate it
			}
			if (throwError) { throw err }
		}
	}

	function networkErrorReload() { // NOSONAR
		window.location.reload()
	}

	function networkErrorTerminate() {
		if (runtime.value.isEmbedded) {
			const electron = useElectronStore()

			electron.terminate()
		} else {
			window.location.href = 'https://blank.page'
		}
	}

	/**
	 * Initialize the app after login or validate
	 * @param data Login or validate response
	 */
	async function initAfterLoginOrValidate(data: ValidateTokenExtendedResponse) {
		auth.value = data
		if (data.token) { token.value = data.token } // Only provided on login

		authenticatedAt.value = new Date()

		api.init(apiUrl.value, token.value as string)

		logger.host = runtime.value.instanceId
		logger.app = `${runtime.value.appName} ${runtime.value.version}${runtime.value.wrapperVersion ? 'A' + runtime.value.wrapperVersion : ''}`
		logger.http = toRefs(api).http

		_i18n.global.locale.value = auth.value.language as 'en' | 'da'

		await loadSettings()

		logger.info('Settings: Auth and Settings loaded - emitting Auth:LoggedIn to load the rest')
		setTimeout(() => {
			globalEvents.emit('Auth:LoggedIn', data)
		}, 10)
	}

	/**
	 * Check for network connection to the server until it is back and then re-initialize the app
	 * @param requestHasFailed True if invoked by Axios interceptor because a request failed
	 */
	async function checkForNetwork(requestHasFailed?: boolean) {
		logger.warning('Checking for network: Starting')

		// Check if it was a single failure that we can recover from
		if (requestHasFailed) {
			try {
				// Must go through the api store: /ping sits behind token validation, so a bare
				// axios call has no Authorization header and always comes back 401
				await api.get('/ping', { timeout: 3000 })
				logger.warning('Settings:Checking for network: Ping check succeeded')
				runtime.value.networkError = false
				runtime.value.checkForNetworkRunning = false
				return // It was a single failure, we can continue
			}
			catch (_) {
				runtime.value.networkError = true
				logger.error('Settings:Checking for network: Failed - we are offline')
			}
		}

		await sleep(2000)

		if (runtime.value.networkError) {
			try {
				await loadAuth(undefined, true)
				logger.warning('Settings:Check for network: Network is back - re-init started')
				runtime.value.checkForNetworkRunning = false
				runtime.value.networkError = false
				if (questionModalVisible.value && questionModalCurrent.value?.header === i18n.t('common.error.commErrorHeader')) {
					globalEvents.emit('CommonQuestionModalDismiss')
				}
			}
			catch (_) {
				if (runtime.value.networkError) { checkForNetwork() }
			}
		}
	}

	/** Instruct the wrapper app to enable/disable the global shortcuts */
	async function initGlobalShortcuts() {
		if (runtime.value.isEmbedded) {
			const electron = useElectronStore()
			const sc: Record<string, string> = {}

			for (const _key of Object.keys(settings.value.globalShortcuts)) {
				const key = _key as keyof PbxAppSettingsDesktopResponse['globalShortcuts']
				const item = settings.value.globalShortcuts[key]

				if (item.enabled) {
					sc[key] = (item.commandOrControl ? 'CommandOrControl+' : '') + (item.alt ? 'Alt+' : '') + (item.shift ? 'Shift+' : '') + (item.key)
				}
			}

			const success = await electron.setGlobalShortcuts(sc)
			if (!success) {
				globalEvents.emit('CommonErrorModal', { header: i18n.t('settings.globalShortcuts'), message: i18n.t('settings.globalShortcutsFailedMessage') })
			}
		}
	}

	/**
	 * Save a generic value to local storage
	 * @param key Name of key
	 * @param value Value to store
	 */
	function setGeneric<T>(key: string, value: T) {
		localStorage.setItem('Generic-' + key, JSON.stringify(value))
	}

	/**
	 * Get a generic value from local storage
	 * @param key Name of key
	 * @returns Saved value or undefined if not found
	 */
	function getGeneric<T>(key: string) {
		const value = localStorage.getItem('Generic-' + key)

		return value ? JSON.parse(value) as T : undefined
	}

	/**
	 * Removes national prefix from phone number if present
	 * @param number Phone number in E.164 format to work on
	 * @param format True to format number with spaces
	 * @param noSecret True if the presence of text in the number field should not be replaced
	 */
	function cutNationalPrefix(number: string, format = false, noSecret = false) {
		if (!number || typeof number !== 'string') { return number }

		if (format && !noSecret && (number === 'SECRET' || /[a-zA-Z]+/.test(number))) { return i18n.t('common.secret') }

		if (number?.startsWith(pbxSettings.value.nationalPrefix) && number.length > pbxSettings.value.nationalPrefix.length) {
			const res = number.replace(pbxSettings.value.nationalPrefix, '')

			return format && !res.startsWith('+') ? res.match(/.{2}/g)?.join(' ') as string : res
		} else if (format && /^\d{8,}$/.test(number)) {
			return number.match(/.{1,2}/g)?.join(' ') ?? number
		} else {
			return number
		}
	}

	async function hotdeskGrab(id: string, extensionId: string) {
		try {
			logger.debug('Requesting hotdesk grab for')
			const body: PbxSipPhoneHotdeskGrabByIdRequest = { hotdeskId: id, extensionId: extensionId }
			const res = await api.post<PbxSipPhoneHotdeskGrabByIdResponse>('/pbx/sipphone/hotdesk', body)

			logger.debug('Hotdesk grab response received - now setting settings for phoneId')

			if (res.data.success) {
				// globalEvents.emit('EmployeeReloadMyExtensions')
				settings.value.softphoneId = null
				settings.value.phoneId = res.data.sipPhoneId
				logger.info(`Settings: Switched phone to ${settings.value.phoneId}`)
			}
		}
		catch (err) {
			logger.error(`Settings: Failed to grab hotdesk phone: ${(err as Error).message}`)
		}
		logger.debug('Settings done')
	}

	/**
	 * Release the current hotdesk phone
	 */
	async function hotdeskRelease() {
		if (!realPhoneId.value) { return }

		try {
			await api.del(`/pbx/sipphone/${realPhoneId.value}/hotdesk`)
			settings.value.softphoneId = null
			settings.value.phoneId = null
			logger.info('Settings: Released hotdesk phone')
			globalEvents.emit('EmployeeReloadMyExtensions')
		}
		catch (err) {
			logger.error(`Settings: Failed to release hotdesk phone: ${(err as Error).message}`)
		}
	}

	/**
	 * Check if the current SIP phone is a hotdesk phone
	 * @returns True if current SIP phone is a hotdesk phone
	 */
	async function hotdeskIsCurrent(): Promise<boolean> {
		if (!realPhoneId.value) { return false }

		try {
			const res = await api.get<PbxSipPhone>(`/pbx/sipphone/${realPhoneId.value}`)

			return res.data.hotdesk ?? false
		}
		catch (_) { return false } // Not found when phone is a Mobile phone which cannot be a hotdesk phone.
	}

	/** Returns when we are authenticated and ready to use the server API */
	async function ready() {
		if (isAuthenticated.value) { return }
		await sleep(250)
		return ready()
	}

	// #region Initialize the app

	async function mainInit() {
		window.document.title = runtime.value.appName

		// Handle query args when started from the wrapper app
		const query = parseQuery(document.location.search)

		// Passkey enrollment link - the API sends this hostname's enrollUrl as #passkeyToken=…, a fragment so
		// the single use token never reaches the web server's access log or a referrer header. A malformed
		// percent-escape (a link mangled by an email client) must not throw and stall the rest of mainInit()
		let fragment: Record<string, string> = {}

		try { fragment = parseQuery(document.location.hash.replace(/^#/, '')) }
		catch (_) { /** Ignore - behave as if there were no token */ }

		if (fragment.passkeyToken) {
			runtime.value.passkeyToken = fragment.passkeyToken
			// Drop it from the address bar so a screenshot or a shared link cannot leak it
			history.replaceState(null, '', document.location.pathname + document.location.search)
		}

		if (query.embedded) {
			runtime.value.isEmbedded = true
			runtime.value.isEmbeddedIpc = query.embedded === 'IPC'
			if (query.port) { runtime.value.localUrl = 'http://localhost:' + query.port }
			if (query.version) { runtime.value.wrapperVersion = query.version }

			// TODO: This is needed during transition. When the backend app is updated, this should be removed
			const electron = useElectronStore() // Important to do there and not globally to avoid circular dependencies

			if (!runtime.value.wrapperVersion) {
				try {
					const pingRes = await electron.ping()

					if (pingRes) { runtime.value.wrapperVersion = pingRes }
				}
				catch (_) { /** Ignore */ }
			}

			electron.setAlwaysOnTop(settings.value.alwaysOnTop)
		}

		globalEvents.on('Auth:LoggedOut', (originator: string) => {
			if (originator !== 'SETTINGS') {
				logger.debug(`Settings: Detected LoggedOut by ${originator}  - now performing logout`)
				logout(false, true)
			}
		})

		globalEvents.on('RequestHasFailed', () => {
			if (!runtime.value.checkForNetworkRunning) {
				runtime.value.checkForNetworkRunning = true
				checkForNetwork(true)
			}
		})

		globalEvents.on('Network:Error:Retry', networkErrorReload)
		globalEvents.on('Network:Error:Terminate', networkErrorTerminate)

		// Load settings from local storage
		runtime.value.rememberMe = bool(localStorage.getItem('rememberMe') as string)
		const encryptedToken = runtime.value.rememberMe ? localStorage.getItem('token') : sessionStorage.getItem('token')

		runtime.value.emailAddress = localStorage.getItem('emailAddress') ?? undefined

		if (encryptedToken?.startsWith('eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.')) {
			// TODO: Remove this code after a while - this is to handle the transition where we had unencrypted tokens in local/session storage
			// Token is not encrypted - legacy from before we added encryption - type to encrypt it
			token.value = encryptedToken

			const cryptoStorage = new CryptoStorage(getInstanceId())

			cryptoStorage.encrypt(encryptedToken).then(encrypted => {
				if (runtime.value.rememberMe) {
					localStorage.setItem('token', encrypted)
				} else {
					sessionStorage.setItem('token', encrypted)
				}
			}).catch(() => { /** Ignore */ })
		} else if (encryptedToken) {
			try {
				const cryptoStorage = new CryptoStorage(getInstanceId())
				const decryptedToken = await cryptoStorage.decrypt(encryptedToken)

				token.value = decryptedToken
			}
			catch (_) { /** Ignore */ }
		}

		mainInitCompleted.value = true

		loadAuth(query.impersonate).catch(() => { /** Ignore */ }) // Triggers everything else to load
	}

	// For updating times using a single timer instead of a million
	let dayToday = new Date().getDate()

	setInterval(() => { globalEvents.emit('OneSecond') }, 1000)

	// Emit an event every minute on the whole mimute
	const numberOfSecondsToNextMinute = 60 - new Date().getSeconds()

	setTimeout(() => {
		setInterval(() => {
			globalEvents.emit('OneMinute')
			const currentDayToday = new Date().getDate()
			if (dayToday !== currentDayToday) {
				dayToday = currentDayToday
				globalEvents.emit('Midnight')
			}
		}, 1000 * 60)
	}, numberOfSecondsToNextMinute * 1000)

	document.addEventListener('fullscreenchange', () => {
		if (!document.fullscreenElement && settings.value.fullscreen) {
			settings.value.fullscreen = false
		} else if (document.fullscreenElement && !settings.value.fullscreen) {
			settings.value.fullscreen = true
		}
	})

	globalEvents.on('Auth:LoggedOut', (source: string) => {
		if (source === 'HTTP') {
			logger.info('Settings: Auth:LoggedOut received from HTTP - performing logout')
			logout(false, true)
		}
	})

	return {
		token, auth, pbxSettings, settings, runtime, recordingsModalVisible, voicemailModalVisible, cdrModalVisible, errorModalVisible, questionModalVisible, pickAPhoneVisible,
		isDevelopment, apiUrl, isAuthenticated, realPhoneId, isDashboardEditor, isInDebugMode, accessLevel, mainInitCompleted, mainInit, isAnEmployeeContextMenuVisible,
		clear, checkForNetwork, loadAuth, setGeneric, getGeneric, logout, cutNationalPrefix, ready, endImpersonation, accessLevelIsMin, initAfterLoginOrValidate,
		canTranscribeRecordings, canSummarizeRecordings,
		initGlobalShortcuts, beacons, questionModalCurrent, authenticatedSeconds, hotdeskGrab, hotdeskRelease, hotdeskIsCurrent
	}

	// #endregion
})

/** Generate initial settings */
function initialSettings() : PbxAppSettingsDesktopResponse {
	return {
		autoLaunch: false,
		autoLaunchHide: false,
		fullscreen: false,
		globalShortcuts: {
			ANSWER: { key: 'a', commandOrControl: true, shift: true, alt: false, enabled: false },
			HANGUP: { key: 'h', commandOrControl: true, shift: true, alt: false, enabled: false },
			TOGGLE_WINDOW: { key: 'w', commandOrControl: true, shift: true, alt: false, enabled: false },
			CALL:	{ key: 'c', commandOrControl: true, shift: true, alt: false, enabled: false },
			SEARCH: { key: 's', commandOrControl: true, shift: true, alt: false, enabled: false }
		},
		dashboardId: null,
		phoneId: null,
		softphoneId: null,
		presenceBackground: false,
		queueCriticalSound: false,
		ringtoneExternal: '60af91d485fa792a658f75e5', // Marimba
		ringtoneLocal: '619e5e4daa69b00a66f57f75', // Marimba 2
		ringtoneQueue: null,
		showStatusPanel: false,
		theme: 'EARTH',
		windowWallboardMode: false,
		dashboardColumnWidth: 370,
		window: { x: 0, y: 0, width: 1280, height: 800 },
		zoom: 100,
		dndWhenDesktopLocked: false,
		popupOnIncomingCall: false,
		popupRevertWhenDone: false,
		callBackground: false,
		alwaysOnTop: false,
		beaconsMayUpdateLocation: false,
		integration: {
			run: {
				when: PbxAppSettingsIntegrationRunWhen.NEVER,
				delay: undefined,
				command: undefined
			},
			sendkeys: {
				when: PbxAppSettingsIntegrationRunWhen.NEVER,
				delay: undefined,
				focusProcess: undefined,
				maximizeProcess: undefined,
				useAutoit: undefined,
				keys: undefined,
				requireArgs: undefined
			},
			copyToClipboard: {
				when: PbxAppSettingsIntegrationRunWhen.NEVER,
				what: undefined,
				requireArgs: undefined
			},
			browser: {
				when: PbxAppSettingsIntegrationRunWhen.NEVER,
				website: undefined,
				spa: undefined,
				actions: undefined,
				requireArgs: undefined
			},
			website: {
				when: PbxAppSettingsIntegrationRunWhen.NEVER,
				delay: 0,
				url: undefined,
				preferWindow: false
			}
		},
		media: {
			preferredAudioInputs: [],
			preferredAudioOutputs: [],
			preferredVideoInputs: [],
			preferredRingerOutputs: [],
			ringerVolume: 0.5,
			kuando: {
				use: false,
				ringTone: 0,
				ringVolume: 0,
				useColorSettings: false,
				colorSettings: null
			},
			preferredHeadset: PbxAppSettingsMediaPreferredHeadset.NONE
		},
		queueRingtones: [],
		outboundGoto: false
	}
}

function initialRuntimeSettings() : RuntimeSettings {
	return {
		appName: 'Communicator',
		version: __APP_VERSION__,
		checkForNetworkRunning: false,
		gotStash: false,
		isEmbedded: false,
		isEmbeddedIpc: false,
		networkError: false,
		localUrl: 'http://localhost:61337',
		selectedSection: SectionType.DASHBOARD,
		rememberMe: bool(localStorage.getItem('rememberMe') as string),
		wrapperVersion: '',
		emailAddress: localStorage.getItem('emailAddress') ?? undefined,
		desktopLocked: false,
		instanceId: getInstanceId(),
		visible: true
	}
}

function initialPbxSettings() : PbxSettingsResponse {
	return {
		nationalPrefix: '+45',
		_id: '',
		defaultLanguage: '',
		defaultNumber: '',
		defaultVoiceAccount: '',
		callerIdNameOutbound: PbxSettingsCallerIdNameOutbound.AS_IS,
		recording: {
			mode: EmployeeRecordingMode.ALWAYS,
			quality: EmployeeRecordingQuality.NORMAL,
			retension: 1,
			permissions: { transcribe: false, summarize: false }
		},
		billing: {
			invoicedUntil: undefined
		},
		presentMobileOnLocalToMobile: false
	}
}

/** Parse a URL query string */
function parseQuery(queryString: string) {
	const query: Record<string, string> = {}
	const pairs = (queryString.startsWith('?') ? queryString.slice(1) : queryString).split('&')

	for (const element of pairs) {
		const pair = element.split('=')

		query[decodeURIComponent(pair[0] as string)] = decodeURIComponent(pair[1] || '')
	}
	return query
}

/**
 * Get or generate a unique instance ID
 * @returns Unique instance ID
 */
function getInstanceId() {
	const id = localStorage.getItem('instanceId')
	if (id) { return id }

	let length = 32
	let buffer = ''
	const source = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'

	while (length) {
		buffer += source[Math.floor(Math.random() * source.length)]
		length--
	}

	localStorage.setItem('instanceId', buffer)
	return buffer
}
